// ═══════════════════════════════════════════════════════════════════════════════
// System message Zod schemas
// ═══════════════════════════════════════════════════════════════════════════════
import { z } from 'zod';
import { USER_ROLE } from '../constants/index.js';
import { USER_LEVEL } from '../types/employerLimits.js';
import { SYSTEM_MESSAGE_MATCH_MODE } from '../types/systemMessage.js';
const hexColorSchema = z
    .string()
    .trim()
    .regex(/^#?[0-9a-fA-F]{6}$/, 'Invalid hex color');
const optionalSubject = z.string().trim().max(100).optional();
const optionalDetail = z.string().max(10_000).optional();
const targetRolesSchema = z
    .array(z.enum([USER_ROLE.EMPLOYER, USER_ROLE.HELPER]))
    .max(2)
    .optional();
const targetLevelsSchema = z
    .array(z.union([
    z.literal(USER_LEVEL.TRIAL),
    z.literal(USER_LEVEL.FREE),
    z.literal(USER_LEVEL.PAID),
]))
    .max(3)
    .optional();
/** Optional UTC wall time: YYYY-MM-DD hh:mm, or null to clear. */
const datetimeOptional = z
    .union([
    z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/, 'Must be YYYY-MM-DD hh:mm (UTC)'),
    z.null(),
])
    .optional();
const baseFields = {
    isActive: z.boolean().optional(),
    subjectZhHant: optionalSubject,
    subjectEn: optionalSubject,
    detailZhHant: optionalDetail,
    detailEn: optionalDetail,
    displayFrom: datetimeOptional,
    displayTo: datetimeOptional,
    targetRoles: targetRolesSchema,
    targetLevels: targetLevelsSchema,
    targetMatchMode: z
        .enum([SYSTEM_MESSAGE_MATCH_MODE.AND, SYSTEM_MESSAGE_MATCH_MODE.OR])
        .optional(),
    showOnEmployerDashboard: z.boolean().optional(),
    showOnHelperHome: z.boolean().optional(),
    tickerBgColor: hexColorSchema.optional(),
    notifyInApp: z.boolean().optional(),
    notifyWebPush: z.boolean().optional(),
    notifyEmail: z.boolean().optional(),
};
const assertHasSubject = (data, ctx) => {
    const zh = data.subjectZhHant?.trim() ?? '';
    const en = data.subjectEn?.trim() ?? '';
    if (!zh && !en) {
        ctx.addIssue({
            code: 'custom',
            message: 'At least one of subjectZhHant or subjectEn is required',
            path: ['subjectZhHant'],
        });
    }
};
export const createSystemMessageInputSchema = z
    .object({
    ...baseFields,
    confirmSend: z.literal(true),
})
    .superRefine(assertHasSubject);
export const updateSystemMessageInputSchema = z
    .object({
    ...baseFields,
    subjectZhHant: z.union([z.string().trim().max(100), z.null()]).optional(),
    subjectEn: z.union([z.string().trim().max(100), z.null()]).optional(),
    detailZhHant: z.union([z.string().max(10_000), z.null()]).optional(),
    detailEn: z.union([z.string().max(10_000), z.null()]).optional(),
})
    .superRefine((data, ctx) => {
    if (data.subjectZhHant === undefined && data.subjectEn === undefined) {
        return;
    }
    const zh = data.subjectZhHant === null ? '' : (data.subjectZhHant?.trim() ?? '__keep__');
    const en = data.subjectEn === null ? '' : (data.subjectEn?.trim() ?? '__keep__');
    if (zh === '' && en === '') {
        ctx.addIssue({
            code: 'custom',
            message: 'At least one of subjectZhHant or subjectEn is required',
            path: ['subjectZhHant'],
        });
    }
});
export const estimateSystemMessageAudienceInputSchema = z
    .object(baseFields)
    .superRefine(assertHasSubject);
//# sourceMappingURL=systemMessage.schema.js.map